import { getAccessTokenSilently } from "utils/functions";
import { InputField } from "components";
import Button from "components/button/Button";
import StripeCard from "components/StripeCard/StripeCard";
import TranzilaCard from "components/TranzilaCard/TranzilaCard";
import { checkIsStripe, checkIsTranzila } from "helper/checkIsStripe";
import { postalCodeFormat, postalCodeLabel } from "helper/postalCodeFormat";
import AuthService from "Lib/API/Auth";
import React, { useRef, useState } from "react";
import { toastr } from "react-redux-toastr";
import CircularProgress from "@mui/material/CircularProgress";
import { FontWeights, FontColors } from "theme/fontConfig";

export const AddCard = ({ ...props }) => {
  const addCardStripeRef = useRef(null);

  const [cardNumber, setCardNumber] = React.useState(null);
  const [expiryMonth, setExpiryMonth] = React.useState(null);
  const [expiryYear, setExpiryYear] = React.useState(null);
  const [cvc, setCvc] = React.useState(null);
  const [zip, setZip] = React.useState(null);
  const [error, setErrors] = useState("");
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [tranzilaCurrency, setTranzilaCurrency] = useState("ILS");
  let accountID = localStorage.getItem("accountID")
    ? localStorage.getItem("accountID")
    : "";

  const paymentMethod = JSON.parse(
    localStorage.getItem("paymentMethodCreditCard")
  );

  const postalLabel = postalCodeLabel();

  const isStripe = checkIsStripe(paymentMethod);
  const isTranzila = checkIsTranzila(paymentMethod);

  const handleSubmit = async (tokenId = "", tranzilaResult = null) => {
    setErrors("");
    const token = await getAccessTokenSilently();
    const cardDetails = {
      object: "card",
      number: cardNumber?.replace(/\s/g, ""),
      exp_month: expiryMonth,
      exp_year: expiryYear,
      cvc: cvc,
      zip: zip,
    };
    if (
      !isStripe && !isTranzila &&
      (!cardNumber || !expiryMonth || !expiryYear || !cvc || !zip)
    ) {
      return toastr.error("Error", "All card details fields are required");
    }
    const validZipCode = postalCodeFormat(zip);
    if (!isStripe && !isTranzila && !validZipCode) {
      toastr.info("Validation", `Invalid ${postalLabel}`);
    }

    const bodyData = {};
    if (isStripe && tokenId) {
      bodyData.token = tokenId;
    } else if (isTranzila && tokenId && tranzilaResult) {
      bodyData.token = tokenId;
      bodyData.cardsDetails = {
        object: 'card',
        number: tokenId,
        exp_month: tranzilaResult.expmonth ? String(Number(tranzilaResult.expmonth)) : undefined,
        exp_year: tranzilaResult.expyear ? `20${tranzilaResult.expyear}` : undefined,
      };
      bodyData.tranzilaData = tranzilaResult;
      bodyData.currency = tranzilaCurrency;
    } else {
      bodyData.cardsDetails = cardDetails;
    }
    setAddCardLoading(true);
    await AuthService.addCard(bodyData, token, accountID)
      .then((res) => {
        if (res) {
          props.closeModal();
          props.refetchAchCards();
          toastr.success("Success", "Card has been added successfully ");
        }
      })
      .catch((err) => {
        setErrors(err?.data?.message);
        setTimeout(() => {
          setErrors("");
        }, 3000);
      })
      .finally(() => {
        setAddCardLoading(false);
      });
  };

  const handleGetStripeToken = async () => {
    const tokenId = await addCardStripeRef?.current?.handleGetToken();
    if (tokenId && tokenId !== null) {
      handleSubmit(tokenId);
    }
  };

  const handleAddCard = async () => {
    if (isStripe) {
      handleGetStripeToken();
      return;
    }
    handleSubmit();
  };

  return (
    <dv className="add-card">
      <div className="form-title">Add New Card</div>
      {isTranzila ? (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {['ILS', 'USD'].map((cur) => (
              <button
                key={cur}
                type="button"
                onClick={() => setTranzilaCurrency(cur)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  border: tranzilaCurrency === cur ? '2px solid #2555EF' : '1px solid #e2e8f0',
                  background: tranzilaCurrency === cur ? '#EEF2FF' : '#fff',
                  color: tranzilaCurrency === cur ? '#2555EF' : '#333',
                  fontWeight: tranzilaCurrency === cur ? FontWeights.SemiBold : FontWeights.Regular,
                  cursor: 'pointer',
                }}
              >
                {cur === 'ILS' ? '₪ ILS' : '$ USD'}
              </button>
            ))}
          </div>
          <TranzilaCard
            onSaveCard={handleSubmit}
            loading={addCardLoading}
            setLoading={setAddCardLoading}
            saveCard={true}
            currency={tranzilaCurrency}
            terminalName={paymentMethod?.paymentGateway?.keys?.terminal_name}
          />
        </>
      ) : isStripe ? (
        <StripeCard
          onSaveCard={handleAddCard}
          loading={addCardLoading}
          setLoading={setAddCardLoading}
          ref={addCardStripeRef}
          publicKey={paymentMethod?.paymentGateway?.keys?.publicKey}
        />
      ) : (
        <div className="row">
          <div className="col-sm-4 pr-0">
            <InputField
              mandatory
              {...{
                title: "Card Number",
                name: "cardNumber",
                type: "string",
                minlength: "16",
                maxLength: "16",
              }}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              handleChange={(event) => setCardNumber(event.target.value)}
            />
          </div>
          <div className="col-sm-2 pr-0">
            <InputField
              mandatory
              {...{
                title: "Expiry Month",
                name: `exp_month`,
                placeholder: "MM",
                minlength: "1",
                maxLength: "2",
                type: "string",
              }}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              handleChange={(event) => setExpiryMonth(event.target.value)}
            />
          </div>
          <div className="col-sm-2 pr-0">
            <InputField
              mandatory
              {...{
                title: "Expiry Year",
                type: "string",
                minlength: "4",
                maxLength: "4",
                name: `exp_year`,
                placeholder: "YYYY",
              }}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              handleChange={(event) => setExpiryYear(event.target.value)}
            />
          </div>
          <div className="col-sm-2 pr-0">
            <InputField
              mandatory
              {...{
                title: "CVC",
                type: "string",
                minlength: "3",
                maxLength: "4",
                name: `cvc`,
                placeholder: "3-4 digits",
                // value: formik.values?.cardDetails.cvc,
              }}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              handleChange={(event) => setCvc(event.target.value)}
            />
          </div>
          <div className="col-sm-2 pr-0">
            <InputField
              mandatory
              {...{
                title: postalLabel,
                name: `zip`,
                placeholder: postalLabel,
                type: "string",
              }}
              handleChange={(event) => setZip(event.target.value)}
            />
          </div>
        </div>
      )}
      {error && <div className="error-card">{error}</div>}
      <div className="add-button">
        <Button
          style={{ minWidth: "150px" }}
          onClick={() => handleAddCard()}
          variant="primary"
          size="md"
          disabled={addCardLoading}
        >
          Add Card{" "}
          {addCardLoading && (
            <CircularProgress size={16} sx={{ color: FontColors.White }} />
          )}
        </Button>
      </div>
    </dv>
  );
};
